import DOMPurifyFactory from 'dompurify';
import { marked } from 'marked';

import { renderBodyWithChipLinks } from './mentionChipUtils';
import { injectCitationMarkers } from './WebSearch/citationUtils';
import { CitationMarker } from './WebSearch/webSearch.types';

// Tilde strikethrough is not used in agent output. Intercept del patterns and emit
// them as plain text tokens so adjacent formatting like **bold** is never disrupted.
marked.use({
  extensions: [
    {
      name: 'del',
      level: 'inline',
      start: (src: string) => src.indexOf('~'),
      tokenizer(src: string) {
        // Match only the opening tilde(s), not the full span. This prevents del
        // from consuming bold markers (**) that appear between two tildes.
        const match = /^(~~?)(?=[^\s~])/.exec(src);
        if (match) {
          return { type: 'text', raw: match[1], text: match[1] };
        }
        return undefined;
      },
    },
  ],
});

export const CONVERSATIONS_PER_PAGE = 25;

export const wrapTablesForScroll = (html: string): string =>
  html.replace(/<table/g, '<div class="ai-table-scroll"><table').replace(/<\/table>/g, '</table></div>');
export const CONVERSATION_MAX_TITLE_LENGTH = 100;

export type ConversationListItem = {
  id: string;
  title: string;
  lastUserMessageAt?: string | null;
};

// Explicitly create instance with current window to avoid issues when the
// module-level createDOMPurify() runs before DOM is fully ready
const DOMPurify = DOMPurifyFactory(window);

// DOMPurify config that allows mention chip SVG + citation marker attrs to survive sanitisation.
// Kept here so renderMessageHtml and AIInsightsSidebar.tsx can share it (sidebar will migrate to this in Chunk 4).
export const CHIP_DOMPURIFY_OPTIONS = {
  ADD_TAGS: ['svg', 'path', 'circle'],
  ADD_ATTR: [
    'stroke',
    'stroke-width',
    'stroke-linecap',
    'stroke-linejoin',
    'stroke-dasharray',
    'fill-rule',
    'viewBox',
    'xmlns',
    'aria-hidden',
    'data-citation-end',
  ],
};

/**
 * Full markdown render pipeline for assistant message text parts.
 * Injects citation markers first (pre-markdown seam, preserving BE offsets),
 * then injects mention chip links, parses markdown, sanitises HTML, and wraps
 * tables for horizontal scroll.
 */
export const renderMessageHtml = (text: string, citations: CitationMarker[] = []): string => {
  const withCitations = injectCitationMarkers(text, citations);
  const withChips = renderBodyWithChipLinks(withCitations);
  const html = String(marked.parse(withChips));
  return wrapTablesForScroll(DOMPurify.sanitize(html, CHIP_DOMPURIFY_OPTIONS));
};
